import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";

// Same numeric value as PlayDialogDifficulty.Common — kept as a literal rather
// than importing play_dialog.ts, same leaf-module reasoning as
// block_showcase.ts's local PIECE map.
const COMMON_DIFFICULTY = 0;

const PIECE = {
  Cube: 1,
  Stone: 2,
  Jar: 3,
  Cone: 4,
  Metal: 5,
  Ice: 6,
  Tree: 7,
  Plastic: 9,
  RoundMetal: 10,
  IronCan: 11,
} as const;

const HARD_DIFFICULTY = 1;
const SUPER_HARD_DIFFICULTY = 2;

// Mirrors BarrierHead / BarrierMount in entities/barrier.ts. Same reasoning as
// PIECE above: this file stays a leaf that only the debug menu pulls in.
const HEAD = { Blade: 0, Hammer: 1 } as const;
const MOUNT = { Ceiling: 1, Left: 2, Right: 3 } as const;

const COLS = 9;
const ROWS = 10;

interface PieceSpec
{
  type: number;
  x: number;
  /** Defaults to standing on the tabletop. */
  y?: number;
  base?: number;
  width: number;
  height: number;
  variant?: number;
  lying?: boolean;
}

function makeLayer( specs: PieceSpec[], cols = COLS ): LevelLayer
{
  const grid = Array.from( { length: ROWS }, () => Array( cols ).fill( 0 ) );
  const pieces: LevelPiece[] = [];

  specs.forEach( ( spec, index ) =>
  {
    const id = index + 1;
    const y = spec.y ?? ROWS - ( spec.base ?? 0 ) - spec.height;

    pieces.push( {
      id,
      type: spec.type as LevelPieceType,
      x: spec.x,
      y,
      width: spec.width,
      height: spec.height,
      variant: spec.variant,
      lying: spec.lying,
    } );

    for ( let row = y; row < y + spec.height; row++ )
      for ( let col = spec.x; col < spec.x + spec.width; col++ )
        grid[row][col] = id;
  } );

  return { grid, pieces };
}

/** A full-height wall for comparing barrier and block lighting directly. */
function targetWall(): LevelLayer[]
{
  const specs: PieceSpec[] = [];
  for ( let y = 0; y < ROWS; y++ )
    for ( let x = 0; x < COLS; x++ )
      specs.push( {
        type: PIECE.Cube,
        x,
        y,
        width: 1,
        height: 1,
        variant: ( x + y ) % 2 === 0 ? 0 : 2,
      } );

  return [ makeLayer( specs ) ];
}

/** Rows of 7, 5, 3 and 1 cubes centred on the table, a stone crowning it. */
function pyramid(): LevelLayer
{
  const specs: PieceSpec[] = [];
  for ( let step = 0; step < 4; step++ )
  {
    const y = ROWS - 1 - step;
    for ( let x = 1 + step; x <= COLS - 2 - step; x++ )
      specs.push( {
        type: step === 3 ? PIECE.Stone : PIECE.Cube,
        x,
        y,
        width: 1,
        height: 1,
        variant: step === 3 ? undefined : ( x + step ) % 3,
      } );
  }

  return makeLayer( specs );
}

/** Rows of 3, 2 and 1 cubes on a 3-column table — small enough for two tables. */
function smallPyramid(): LevelLayer
{
  const cols = 3;
  const specs: PieceSpec[] = [];
  for ( let step = 0; step < 3; step++ )
    for ( let x = 0; x < cols - step; x++ )
      specs.push( {
        type: PIECE.Cube,
        x: x + ( step === 2 ? 1 : 0 ),
        y: ROWS - 1 - step,
        width: 1,
        height: 1,
        variant: ( x + step ) % 3,
      } );

  return makeLayer( specs, cols );
}

/**
 * Barrier coordinates are in grid cells, measured from the table: `x` across
 * the frame from its middle, `y` up from the tabletop, `z` forwards from its
 * front edge. Reach worth planning around: a propeller sweeps a circle of about
 * 2.25 cells around its bearing, and a hammer swings 3 cells out from its own.
 *
 * Every level needs blocks on the table: the game ends the moment no entity is
 * left alive (GameScreen#checkGameEnd), and barriers are not entities — a
 * barrier-only level would be won before the first shot.
 */
const BARRIER_LEVELS: LevelConfig[] = [
  {
    // Propeller on a rod reaching in from the left edge of the frame.
    level: 90500,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: targetWall(),
    barriers: [
      { head: HEAD.Blade, mount: MOUNT.Left, x: -1.5, y: 2.5 },
    ],
  },
  {
    // Hammer hanging out of the sky — the silhouette from the reference art.
    level: 90501,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: targetWall(),
    barriers: [
      { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 0, y: 5 },
    ],
  },
  {
    // The other combinations: a hammer on a rod from the right edge, swinging
    // up and down with its head towards the middle, and a propeller on a
    // pillar dropped from above. The hammer stands a cell nearer the camera so
    // its swing clears the propeller's circle in depth.
    level: 90502,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: targetWall(),
    barriers: [
      { head: HEAD.Hammer, mount: MOUNT.Right, x: 3, y: 3.5, z: 2 },
      { head: HEAD.Blade, mount: MOUNT.Ceiling, x: -2.5, y: 3 },
    ],
  },
  {
    // A row of them, the way a real level would use barriers: a moving screen
    // the ball has to find a gap in. Spaced 3.5 cells apart so the propellers'
    // circles do not overlap.
    level: 90503,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: targetWall(),
    barriers: [
      { head: HEAD.Blade, mount: MOUNT.Left, x: -3.5, y: 2.5 },
      { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 0, y: 5, angle: 30 },
      { head: HEAD.Blade, mount: MOUNT.Right, x: 3.5, y: 2.5 },
    ],
  },
  {
    // A playable level rather than a wall: a two-deep pyramid the ball can
    // actually knock over, guarded by a propeller on the left and a hammer
    // swinging over the right. The hammer stands a cell nearer the camera so
    // its swing clears the propeller's circle in depth.
    level: 90504,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ pyramid(), pyramid() ],
    barriers: [
      { head: HEAD.Blade, mount: MOUNT.Left, x: -3, y: 2.5 },
      { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 3, y: 6.5, z: 2 },
    ],
  },
  {
    // Barriers on a multi-table level: each table carries its own, positioned
    // from that table rather than from tables[0] or the frame. The left table
    // gets a propeller and a hammer, the right one a single propeller.
    level: 90505,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    tables: [
      {
        slot: { u: -0.8, v: 0 },
        layers: [ smallPyramid() ],
        barriers: [
          { head: HEAD.Blade, mount: MOUNT.Left, x: -2, y: 2.5 },
          { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 1.5, y: 6 },
        ],
      },
      {
        slot: { u: 0.8, v: 0 },
        layers: [ smallPyramid() ],
        barriers: [
          { head: HEAD.Blade, mount: MOUNT.Right, x: 0, y: 2.5 },
        ],
      },
    ],
  },
];

function cubeStack( x: number, from: number, count: number, variant?: number ): PieceSpec[]
{
  return Array.from( { length: count }, ( _, i ) => ( {
    type: PIECE.Cube,
    x,
    base: from + i,
    width: 1,
    height: 1,
    variant: variant ?? ( x + from + i ) % 3,
  } ) );
}

function twinTowers(): LevelLayer
{
  return makeLayer( [
    ...[ 1, 2, 6, 7 ].flatMap( x => cubeStack( x, 0, 5, 1 ) ),
    { type: PIECE.IronCan, x: 3, width: 1, height: 2, variant: 0 },
    { type: PIECE.Jar, x: 4, width: 1, height: 3, variant: 1 },
    { type: PIECE.IronCan, x: 5, width: 1, height: 2, variant: 1 },
    ...[ 1, 2, 6, 7 ].map( ( x, i ) => ( { type: PIECE.Cone, x, base: 5, width: 1, height: 1, variant: 3 + i } ) ),
  ] );
}

function mixedWall(): LevelLayer
{
  return makeLayer( [
    ...Array.from( { length: COLS }, ( _, x ) => cubeStack( x, 0, 3 ) ).flat(),
    { type: PIECE.Metal, x: 1, base: 3, width: 1, height: 3, variant: 0 },
    { type: PIECE.Stone, x: 3, base: 3, width: 1, height: 1 },
    { type: PIECE.Ice, x: 4, base: 3, width: 1, height: 3 },
    { type: PIECE.Stone, x: 5, base: 3, width: 1, height: 1 },
    { type: PIECE.Metal, x: 7, base: 3, width: 1, height: 3, variant: 1 },
  ] );
}

function jarRow(): LevelLayer
{
  return makeLayer( [
    ...Array.from( { length: COLS }, ( _, x ) => ( { type: PIECE.Cube, x, width: 1, height: 1, variant: 2 } ) ),
    ...[ 0, 2, 4, 6, 8 ].map( ( x, i ) => ( { type: PIECE.Jar, x, base: 1, width: 1, height: 3, variant: i % 4 } ) ),
    ...[ 1, 3, 5, 7 ].flatMap( ( x, i ) => [
      ...cubeStack( x, 1, 1, 1 ),
      { type: PIECE.Cone, x, base: 2, width: 1, height: 1, variant: 7 + i },
    ] ),
  ] );
}

function staircase(): LevelLayer
{
  const heights = [ 1, 1, 2, 2, 3, 3, 4, 4, 5 ];

  return makeLayer( heights.flatMap( ( height, x ) => [
    ...cubeStack( x, 0, height - 1 ),
    { type: PIECE.Plastic, x, base: height - 1, width: 1, height: 1, variant: x % 8 },
  ] ) );
}

function keep(): LevelLayer
{
  return makeLayer( [
    { type: PIECE.RoundMetal, x: 0, width: 1, height: 2, variant: 0 },
    ...[ 3, 5 ].flatMap( x => cubeStack( x, 0, 5 ) ),
    ...cubeStack( 4, 0, 1 ),
    ...[ 1, 2, 3 ].map( base => ( { type: PIECE.Stone, x: 4, base, width: 1, height: 1 } ) ),
    ...cubeStack( 4, 4, 1 ),
    ...[ 3, 4, 5 ].map( ( x, i ) => ( { type: PIECE.Cone, x, base: 5, width: 1, height: 1, variant: 8 + i } ) ),
    { type: PIECE.RoundMetal, x: 8, width: 1, height: 2, variant: 1 },
  ] );
}

function arches(): LevelLayer
{
  return makeLayer( [
    ...[ 0, 2, 3, 5, 6, 8 ].flatMap( x => cubeStack( x, 0, 3, 1 ) ),
    ...[ 1, 4, 7 ].map( ( x, i ) => ( { type: PIECE.Jar, x, width: 1, height: 2, variant: i } ) ),
    ...[ 0, 3, 6 ].map( x => ( { type: PIECE.Tree, x, base: 3, width: 3, height: 1, lying: true } ) ),
    ...[ 1, 4, 7 ].map( ( x, i ) => ( { type: PIECE.Plastic, x, base: 4, width: 1, height: 2, variant: i * 2 } ) ),
  ] );
}

function deepWall(): LevelLayer
{
  return makeLayer( [
    ...Array.from( { length: COLS }, ( _, x ) => cubeStack( x, 0, 2 ) ).flat(),
    { type: PIECE.Metal, x: 0, base: 2, width: 1, height: 2, variant: 2 },
    ...[ 2, 3, 5, 6 ].flatMap( x => cubeStack( x, 2, 1 ) ),
    { type: PIECE.Ice, x: 4, base: 2, width: 1, height: 2 },
    { type: PIECE.Metal, x: 8, base: 2, width: 1, height: 2, variant: 3 },
  ] );
}

function plasticPyramid(): LevelLayer
{
  const blocks = [ [ 1, 0 ], [ 3, 0 ], [ 5, 0 ], [ 2, 2 ], [ 4, 2 ], [ 3, 4 ] ];

  return makeLayer(
    blocks.map( ( [ x, base ], i ) => ( { type: PIECE.Plastic, x, base, width: 2, height: 2, variant: i } ) ),
    8
  );
}

function smallTower(): LevelLayer
{
  return makeLayer( [ 0, 1, 2 ].flatMap( x => cubeStack( x, 0, 5 ) ), 3 );
}

const LINE_LEVELS: LevelConfig[] = [
  {
    level: 90510,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ pyramid(), pyramid() ],
    barriers: [
      { head: HEAD.Blade, mount: MOUNT.Left, x: -2.5, y: 2.5 },
    ],
  },
  {
    level: 90511,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ twinTowers() ],
    barriers: [
      { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 0, y: 6, z: 2 },
    ],
  },
  {
    level: 90512,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ mixedWall() ],
    barriers: [
      { head: HEAD.Blade, mount: MOUNT.Ceiling, x: 0, y: 3 },
    ],
  },
  {
    level: 90513,
    difficulty: HARD_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ jarRow() ],
    barriers: [
      { head: HEAD.Hammer, mount: MOUNT.Right, x: 2.5, y: 4.5, z: 2 },
    ],
  },
  {
    level: 90514,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ staircase(), staircase() ],
    barriers: [
      { head: HEAD.Blade, mount: MOUNT.Right, x: 2.5, y: 2.5 },
      { head: HEAD.Hammer, mount: MOUNT.Left, x: -2.5, y: 4.5, z: 2 },
    ],
  },
  {
    level: 90515,
    difficulty: HARD_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ keep(), keep() ],
    barriers: [
      { head: HEAD.Blade, mount: MOUNT.Left, x: -3, y: 3 },
      { head: HEAD.Blade, mount: MOUNT.Right, x: 3, y: 3, angle: 90 },
    ],
  },
  {
    level: 90516,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ arches() ],
    barriers: [
      { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: -2.5, y: 6.5, z: 2 },
      { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 2.5, y: 6.5, z: 2 },
    ],
  },
  {
    level: 90517,
    difficulty: SUPER_HARD_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ deepWall(), deepWall() ],
    barriers: [
      { head: HEAD.Blade, mount: MOUNT.Left, x: -3.5, y: 2.5 },
      { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 0, y: 5.5, z: 2, angle: 30 },
      { head: HEAD.Blade, mount: MOUNT.Right, x: 3.5, y: 2.5 },
    ],
  },
  {
    level: 90518,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ plasticPyramid() ],
    slide: true,
    barriers: [
      { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 0, y: 6.5, z: 2 },
    ],
  },
  {
    level: 90519,
    difficulty: HARD_DIFFICULTY as LevelConfig["difficulty"],
    tables: [
      {
        slot: { u: -0.8, v: 0 },
        layers: [ smallTower() ],
        barriers: [
          { head: HEAD.Blade, mount: MOUNT.Left, x: -1, y: 2.5 },
        ],
      },
      {
        slot: { u: 0.8, v: 0 },
        layers: [ smallPyramid(), smallPyramid() ],
        barriers: [
          { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 0, y: 5, z: 2 },
        ],
      },
    ],
  },
  {
    level: 90520,
    difficulty: SUPER_HARD_DIFFICULTY as LevelConfig["difficulty"],
    tables: [
      {
        slot: { u: -1, v: 0.28 },
        layers: [ smallPyramid() ],
        barriers: [
          { head: HEAD.Blade, mount: MOUNT.Ceiling, x: 0, y: 3 },
        ],
      },
      {
        slot: { u: 0, v: 0.8 },
        layers: [ smallTower() ],
        barriers: [
          { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 0, y: 6, z: 2 },
        ],
      },
      {
        slot: { u: 1, v: 0.28 },
        layers: [ smallPyramid() ],
        barriers: [
          { head: HEAD.Blade, mount: MOUNT.Ceiling, x: 0, y: 3, angle: 90 },
        ],
      },
    ],
  },
];

type ShowcaseEntry = { readonly label: string; readonly level: number };

export class BarrierShowcase
{
  public static readonly TESTS = [
    { label: "BLADE on a LEFT rod", level: 90500 },
    { label: "HAMMER out of the sky", level: 90501 },
    { label: "RIGHT hammer + SKY blade", level: 90502 },
    { label: "ROW of barriers", level: 90503 },
    { label: "PYRAMID guarded by BLADE + HAMMER", level: 90504 },
    { label: "TWO TABLES, barriers per table", level: 90505 },
  ] as const;

  public static readonly LINE = [
    { label: "PYRAMID · BLADE on a LEFT rod", level: 90510 },
    { label: "TWIN TOWERS · HAMMER out of the SKY", level: 90511 },
    { label: "MIXED WALL · BLADE out of the SKY", level: 90512 },
    { label: "JAR ROW · HAMMER on a RIGHT rod", level: 90513 },
    { label: "STAIRCASE · RIGHT blade + LEFT hammer", level: 90514 },
    { label: "KEEP · BLADE GATE left + right", level: 90515 },
    { label: "ARCHES · TWIN HAMMERS from the SKY", level: 90516 },
    { label: "DEEP WALL · blade + hammer + blade", level: 90517 },
    { label: "SLIDING TABLE · HAMMER from the SKY", level: 90518 },
    { label: "TWO TABLES · blade + hammer", level: 90519 },
    { label: "THREE TABLES · blade + hammer + blade", level: 90520 },
  ] as const;

  private static active = false;
  private static line = false;
  private static test_index = 0;
  private static line_index = 0;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate( index: number ): number
  {
    this.active = true;
    this.line = false;
    this.test_index = BarrierShowcase.wrap( index, this.TESTS );
    return this.TESTS[this.test_index].level;
  }

  public static activateLine( index: number ): number
  {
    this.active = true;
    this.line = true;
    this.line_index = BarrierShowcase.wrap( index, this.LINE );
    return this.LINE[this.line_index].level;
  }

  public static beginLevel( level: number ): void
  {
    const test_index = this.TESTS.findIndex( test => test.level === level );
    if ( test_index >= 0 )
    {
      this.activate( test_index );
      return;
    }

    const line_index = this.LINE.findIndex( entry => entry.level === level );
    if ( line_index >= 0 )
    {
      this.activateLine( line_index );
      console.info( `[BarrierLine] ${line_index + 1}/${this.LINE.length}: ${this.LINE[line_index].label} (level ${level})` );
      return;
    }

    this.deactivate();
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static currentLevel(): number
  {
    return this.line ? this.LINE[this.line_index].level : this.TESTS[this.test_index].level;
  }

  public static advance(): number
  {
    return this.line ? this.activateLine( this.line_index + 1 ) : this.activate( this.test_index + 1 );
  }

  public static getLineIndex(): number
  {
    return this.line_index;
  }

  public static getLineLabel( index: number ): string
  {
    return this.LINE[BarrierShowcase.wrap( index, this.LINE )].label;
  }

  public static isShowcaseLevel( level: number ): boolean
  {
    return this.TESTS.some( test => test.level === level ) || this.LINE.some( entry => entry.level === level );
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return BARRIER_LEVELS.find( config => config.level === level )
      ?? LINE_LEVELS.find( config => config.level === level )
      ?? null;
  }

  private static wrap( index: number, entries: readonly ShowcaseEntry[] ): number
  {
    return ( index % entries.length + entries.length ) % entries.length;
  }
}
